import { useEffect, useState } from 'react'
import { HiPlus } from 'react-icons/hi'
import { isValidHexColor } from '@utils/utilities'
import {
  IUnifiedPickerTabConfig,
  TColor,
} from '@pageFeatures/design/unifiedColorPicker/unifiedColorPicker.types'
import { UnifiedColorPicker } from '@pageFeatures/design/unifiedColorPicker/unifiedColorPicker'
import { mapInitialColorToTab } from '@pageFeatures/design/unifiedColorPicker/unifiedColorPicker.utils'

/**
 * Check [~/assets/styles/components/brief-color-picker.css]
 * to see the styles overrides for ChromePicker
 * */

interface IBriefColorPicker {
  label: string
  className?: string
  initialValue?: string
  canBeRemoved?: boolean
  onRemove?: () => void
  onChange: (color) => void
  readOnly?: boolean
  config: IUnifiedPickerTabConfig[]
}

export function BriefColorPicker({
  label,
  className,
  initialValue,
  canBeRemoved = false,
  onRemove,
  onChange,
  readOnly = false,
  config,
}: IBriefColorPicker) {
  const [selectedColor, setSelectedColor] = useState<string>(initialValue)
  const [pickerIsOpen, setPickerIsOpen] = useState<boolean>(false)
  const [mappedIntialColorConfig, setMappedInitialColorConfig] =
    useState<TColor[]>(null)

  useEffect(() => {
    setSelectedColor(initialValue)
  }, [initialValue])

  useEffect(() => {
    setMappedInitialColorConfig(
      mapInitialColorToTab({
        tabsConfig: config,
        initialValue,
      })
    )
  }, [initialValue])

  return (
    <div
      className={`relative flex items-center h-[56px] px-6 py-4 border-2 bg-core-white border-black-20 rounded-[6px] ${className} ${
        readOnly ? 'pointer-events-none' : 'pointer-events-auto'
      }`}
      onClick={() => setPickerIsOpen(readOnly ? false : !pickerIsOpen)}
    >
      {isValidHexColor(selectedColor) && (
        <div
          className="w-[24px] h-[24px] rounded-full mr-3 border-2 border-core-grey-20"
          style={{
            backgroundColor: selectedColor,
          }}
        />
      )}

      <div className="flex flex-col items-start cursor-pointer">
        <span
          className={`text-[14px] leading-none text-acai ${
            selectedColor && 'mb-2'
          }`}
        >
          {label}
        </span>
        <span className="text-[11px] leading-none text-acai-40 uppercase">
          {selectedColor}
        </span>
      </div>

      <button
        className={`w-[40px] h-[40px] flex items-center justify-center ml-auto translate-x-5 ${
          readOnly ? 'pointer-events-none' : 'pointer-events-auto'
        }`}
        onClick={(e) => {
          if (selectedColor) {
            e.stopPropagation()
            setSelectedColor('')
            onChange('')

            if (canBeRemoved) {
              onRemove()
            }
          }
        }}
      >
        <HiPlus
          className={`text-2xl font-light transition-all text-black-40 ${
            selectedColor ? 'rotate-45' : ''
          }`}
        />
      </button>

      {pickerIsOpen && (
        <div
          className="w-full max-w-[320px] absolute top-[calc(100%+8px)] left-0 rounded-[16px] bg-core-white border-2 border-black-20 shadow-lg z-1"
          onClick={(e) => e.stopPropagation()}
          onMouseLeave={() => setPickerIsOpen(false)}
        >
          <UnifiedColorPicker
            tabs={config}
            initialColors={mappedIntialColorConfig}
            onSelected={(color) => {
              if (color.value) {
                onChange(color.value)
                setSelectedColor(color.value)
              }
            }}
            onClose={() => setPickerIsOpen(false)}
            className="rounded-3xl"
          />
        </div>
      )}
    </div>
  )
}
